/**
 * TrackerV2 日志工具
 * 支持在开发阶段配置日志打印，方便验证埋点数据
 *
 * 启用方式：
 * 1. URL 参数：?__tracker_log=1
 * 2. localStorage：localStorage.setItem('tracker_v2_log', '1')
 * 3. 代码调用：TrackerLogger.enable()
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  prefix: string;
  showTimestamp: boolean;
  showEventData: boolean; // 是否显示完整的事件数据
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const STORAGE_KEY = "tracker_v2_log";
const URL_PARAM = "__tracker_log";

class TrackerLoggerClass {
  private config: LoggerConfig = {
    enabled: false,
    level: "debug",
    prefix: "🔍 [TrackerV2]",
    showTimestamp: true,
    showEventData: true,
  };

  constructor() {
    this.init();
  }

  /**
   * 初始化：检查 URL 参数和 localStorage
   */
  private init(): void {
    if (typeof window === "undefined") return;

    // 检查 URL 参数
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get(URL_PARAM) === "1") {
      this.config.enabled = true;
      // 持久化到 localStorage
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch (e) {
        // ignore
      }
    }

    // 检查 localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "1") {
        this.config.enabled = true;
      }
    } catch (e) {
      // ignore
    }

    // 开发环境默认启用
    if (process.env.NODE_ENV === "development") {
      this.config.enabled = true;
    }
  }

  /**
   * 启用日志
   */
  enable(): void {
    this.config.enabled = true;
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, "1");
      }
    } catch (e) {
      // ignore
    }
    console.log(`${this.config.prefix} 日志已启用`);
  }

  /**
   * 禁用日志
   */
  disable(): void {
    this.config.enabled = false;
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      // ignore
    }
    console.log(`${this.config.prefix} 日志已禁用`);
  }

  /**
   * 检查是否启用
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * 设置日志级别
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  /**
   * 格式化时间戳
   */
  private formatTimestamp(): string {
    if (!this.config.showTimestamp) return "";
    const now = new Date();
    return `[${now.toLocaleTimeString()}.${now
      .getMilliseconds()
      .toString()
      .padStart(3, "0")}]`;
  }

  /**
   * 通用日志方法
   */
  private log(
    level: LogLevel,
    module: string,
    message: string,
    data?: any
  ): void {
    if (!this.config.enabled) return;
    if (LOG_LEVELS[level] < LOG_LEVELS[this.config.level]) return;

    const timestamp = this.formatTimestamp();
    const prefix = `${this.config.prefix} ${timestamp} [${module}]`;

    const logFn = console[level] || console.log;

    if (data !== undefined && this.config.showEventData) {
      logFn(`${prefix} ${message}`, data);
    } else {
      logFn(`${prefix} ${message}`);
    }
  }

  // ==================== 模块专用日志 ====================

  /**
   * Track 组件日志
   */
  track(
    action: "register" | "unregister" | "click" | "report" | "expose",
    event: string,
    data?: any
  ): void {
    const actionMap = {
      register: "📝 注册",
      unregister: "🗑️ 注销",
      click: "👆 点击",
      report: "📤 上报",
      expose: "👁️ 曝光",
    };
    this.log("info", "Track", `${actionMap[action]}: ${event}`, data);
  }

  /**
   * NavigationContext 日志
   */
  navigation(action: "init" | "update" | "clear", data?: any): void {
    const actionMap = {
      init: "🚀 初始化",
      update: "🔄 页面切换",
      clear: "🧹 清除",
    };
    this.log("info", "Navigation", actionMap[action], data);
  }

  /**
   * Reporter 日志
   */
  report(
    action: "send" | "buffer" | "flush" | "retry" | "offline",
    event?: string,
    data?: any
  ): void {
    const actionMap = {
      send: "📤 发送",
      buffer: "📦 缓冲",
      flush: "🚿 刷新",
      retry: "🔄 重试",
      offline: "📴 离线缓存",
    };
    const msg = event ? `${actionMap[action]}: ${event}` : actionMap[action];
    this.log("info", "Reporter", msg, data);
  }

  /**
   * Validator 日志
   */
  validate(
    event: string,
    valid: boolean,
    errors?: string[],
    warnings?: string[]
  ): void {
    if (valid && (!warnings || warnings.length === 0)) {
      this.log("debug", "Validator", `✅ ${event} 校验通过`);
    } else if (valid && warnings && warnings.length > 0) {
      this.log("warn", "Validator", `⚠️ ${event} 校验警告`, warnings);
    } else {
      this.log("error", "Validator", `❌ ${event} 校验失败`, errors);
    }
  }

  /**
   * Enricher 日志
   */
  enrich(event: string, before: any, after: any): void {
    this.log("debug", "Enricher", `🔧 ${event} 数据增强`, { before, after });
  }

  /**
   * 事件完整日志（用于最终验证）
   * 仅在开发模式下打印
   */
  event(
    eventName: string,
    eventData: any,
    target: "hb_web_test" | "hb_web" = "hb_web_test"
  ): void {
    // 仅在开发模式下打印
    if (process.env.NODE_ENV !== "development") {
      return;
    }

    const emoji = target === "hb_web_test" ? "🆕" : "📦";
    console.group(`${this.config.prefix} ${emoji} ${eventName} → ${target}`);
    console.log("📊 事件数据:", eventData);
    console.log("📍 page_nm:", eventData.page_nm || "(空)");
    console.log("📍 sub_page_nm:", eventData.sub_page_nm || "(空)");
    console.log("🔗 referrer:", eventData.referrer || "(空)");
    console.log("🔗 source_page:", eventData.source_page || "(空)");
    console.groupEnd();
  }

  // ==================== 通用日志方法 ====================

  debug(module: string, message: string, data?: any): void {
    this.log("debug", module, message, data);
  }

  info(module: string, message: string, data?: any): void {
    this.log("info", module, message, data);
  }

  warn(module: string, message: string, data?: any): void {
    this.log("warn", module, message, data);
  }

  error(module: string, message: string, data?: any): void {
    this.log("error", module, message, data);
  }
}

// 导出单例
export const TrackerLogger = new TrackerLoggerClass();

// 挂载到 window 方便调试
if (typeof window !== "undefined") {
  (window as any).TrackerLogger = TrackerLogger;
}
